import type { TerrainKind, PropKind } from './terrain';
import { TERRAIN } from './terrain';

export type ChunkKind = 'wilderness' | 'town' | 'village' | 'dungeon';
export type Biome = 'plains' | 'forest' | 'swamp' | 'graveyard' | 'ruins' | 'cave' | 'crypt';

export interface PropSpawn {
  x: number;
  y: number;
  kind: PropKind;
}

export type NpcRole = 'vendor' | 'innkeep' | 'recruiter' | 'lorekeeper';

export interface NpcSpawn {
  x: number;
  y: number;
  role: NpcRole;
  name: string;
  greeting: string;
}

export interface Chunk {
  id: string;
  kind: ChunkKind;
  biome: Biome;
  depth: number;
  width: number;
  height: number;
  terrain: TerrainKind[][];          // [y][x]
  elevation: number[][];             // [y][x], 0..N
  playerStart: { x: number; y: number };
  exit: { x: number; y: number };
  enemySpawns: { x: number; y: number; type: string }[];
  lootSpawns: { x: number; y: number; item: string }[];
  props: PropSpawn[];
  npcs?: NpcSpawn[];
  townName?: string;
  /** Tile that triggers entry to a dungeon (only present on wilderness chunks
   *  that have a dungeon entrance). */
  dungeonEntrance?: { x: number; y: number };
}

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function elevationAt(chunk: Chunk, x: number, y: number): number {
  if (x < 0 || y < 0 || x >= chunk.width || y >= chunk.height) return 0;
  return chunk.elevation[y][x];
}

export function terrainAt(chunk: Chunk, x: number, y: number): TerrainKind | null {
  if (x < 0 || y < 0 || x >= chunk.width || y >= chunk.height) return null;
  return chunk.terrain[y][x];
}

export function isWalkable(chunk: Chunk, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= chunk.width || y >= chunk.height) return false;
  if (!TERRAIN[chunk.terrain[y][x]].walkable) return false;
  // Props can block.
  for (const p of chunk.props) {
    if (p.x === x && p.y === y) {
      const propStyle = PROP_BLOCKING[p.kind];
      if (propStyle) return false;
    }
  }
  return true;
}

const PROP_BLOCKING: Partial<Record<PropKind, boolean>> = {
  tree: true,
  pine: true,
  rock: true,
  deadtree: true,
};

/**
 * Can we step from (fx,fy) to (tx,ty) given elevation rules?
 * Same elevation: always OK. Different elevation: only via ramp on either side.
 */
export function canTraverseElevation(chunk: Chunk, fx: number, fy: number, tx: number, ty: number): boolean {
  const ez1 = elevationAt(chunk, fx, fy);
  const ez2 = elevationAt(chunk, tx, ty);
  if (ez1 === ez2) return true;
  if (Math.abs(ez1 - ez2) !== 1) return false; // no skipping levels
  const t1 = terrainAt(chunk, fx, fy);
  const t2 = terrainAt(chunk, tx, ty);
  if (!t1 || !t2) return false;
  // One of the two must be a ramp pointing toward the other (we keep it loose:
  // any ramp on either tile permits a single-step elevation change).
  const r1 = TERRAIN[t1].ramp;
  const r2 = TERRAIN[t2].ramp;
  return !!(r1 || r2);
}

// ---- biome palettes --------------------------------------------------------

interface BiomePalette {
  ground: { kind: TerrainKind; weight: number }[];
  water?: TerrainKind;
  obstacle: TerrainKind;
  propCount: number;
  props: { kind: PropKind; weight: number }[];
  elevationVariance: number; // chance to lift a region
  enemyPool: string[];
}

const PALETTES: Record<Biome, BiomePalette> = {
  plains: {
    ground: [{ kind: 'grass', weight: 75 }, { kind: 'dirt', weight: 20 }, { kind: 'stone', weight: 5 }],
    water: 'water',
    obstacle: 'wall',
    propCount: 16,
    props: [{ kind: 'shrub', weight: 50 }, { kind: 'tree', weight: 25 }, { kind: 'rock', weight: 25 }],
    elevationVariance: 0.18,
    enemyPool: ['goblin'],
  },
  forest: {
    ground: [{ kind: 'grass', weight: 55 }, { kind: 'dirt', weight: 35 }, { kind: 'stone', weight: 10 }],
    obstacle: 'wall',
    propCount: 38,
    props: [{ kind: 'tree', weight: 35 }, { kind: 'pine', weight: 35 }, { kind: 'shrub', weight: 25 }, { kind: 'rock', weight: 5 }],
    elevationVariance: 0.22,
    enemyPool: ['goblin', 'skeleton_archer'],
  },
  swamp: {
    ground: [{ kind: 'mud', weight: 55 }, { kind: 'grass', weight: 30 }, { kind: 'dirt', weight: 15 }],
    water: 'water',
    obstacle: 'wall',
    propCount: 20,
    props: [{ kind: 'deadtree', weight: 50 }, { kind: 'shrub', weight: 35 }, { kind: 'rock', weight: 15 }],
    elevationVariance: 0.05,
    enemyPool: ['goblin', 'orc_brute'],
  },
  graveyard: {
    ground: [{ kind: 'dirt', weight: 50 }, { kind: 'stone', weight: 30 }, { kind: 'ash', weight: 20 }],
    obstacle: 'wall',
    propCount: 28,
    props: [{ kind: 'gravestone', weight: 70 }, { kind: 'deadtree', weight: 20 }, { kind: 'rock', weight: 10 }],
    elevationVariance: 0.1,
    enemyPool: ['skeleton_archer', 'orc_brute'],
  },
  ruins: {
    ground: [{ kind: 'stone', weight: 60 }, { kind: 'dirt', weight: 25 }, { kind: 'ash', weight: 15 }],
    obstacle: 'wall',
    propCount: 22,
    props: [{ kind: 'rubble', weight: 50 }, { kind: 'rock', weight: 25 }, { kind: 'crystal', weight: 25 }],
    elevationVariance: 0.25,
    enemyPool: ['orc_brute', 'skeleton_archer'],
  },
  cave: {
    ground: [{ kind: 'stone', weight: 80 }, { kind: 'dirt', weight: 20 }],
    obstacle: 'wall',
    propCount: 30,
    props: [{ kind: 'rock', weight: 60 }, { kind: 'crystal', weight: 25 }, { kind: 'rubble', weight: 15 }],
    elevationVariance: 0,
    enemyPool: ['goblin', 'orc_brute'],
  },
  crypt: {
    ground: [{ kind: 'stone', weight: 70 }, { kind: 'ash', weight: 30 }],
    obstacle: 'wall',
    propCount: 26,
    props: [{ kind: 'gravestone', weight: 60 }, { kind: 'rubble', weight: 30 }, { kind: 'crystal', weight: 10 }],
    elevationVariance: 0,
    enemyPool: ['skeleton_archer', 'orc_brute'],
  },
};

function pickWeighted<T>(rand: () => number, choices: { kind: T; weight: number }[]): T {
  const total = choices.reduce((a, c) => a + c.weight, 0);
  let r = rand() * total;
  for (const c of choices) {
    if (r < c.weight) return c.kind;
    r -= c.weight;
  }
  return choices[choices.length - 1].kind;
}

interface GenOpts {
  depth: number;
  width: number;
  height: number;
  seed: number;
  biome?: Biome;
}

/**
 * Pick a biome by depth. Deeper = more variety / harsher.
 */
export function biomeForDepth(rand: () => number, depth: number): Biome {
  if (depth <= 2) return 'plains';
  if (depth <= 4) return rand() < 0.5 ? 'plains' : 'forest';
  if (depth <= 6) return rand() < 0.4 ? 'forest' : 'swamp';
  if (depth <= 8) return rand() < 0.5 ? 'swamp' : 'graveyard';
  return rand() < 0.5 ? 'graveyard' : 'ruins';
}

/**
 * Generate a wilderness chunk for the given biome.
 *
 * Strategy:
 *  - fill ground from biome palette
 *  - lay 1–2 elevation "plateaus" with surrounding ramps for `forest`/`ruins`
 *  - punch a small water patch off-spine if biome has water
 *  - scatter biome-appropriate props (avoiding the corridor and start/exit)
 *  - sprinkle enemies + a few loot bags
 *  - carve a guaranteed-clear corridor through the middle for traversal
 */
export function generateChunk({ depth, width, height, seed, biome }: GenOpts): Chunk {
  const rand = mulberry32(seed + depth * 31);
  const chosenBiome: Biome = biome ?? biomeForDepth(rand, depth);
  const pal = PALETTES[chosenBiome];

  const terrain: TerrainKind[][] = [];
  const elevation: number[][] = [];
  for (let y = 0; y < height; y++) {
    const trow: TerrainKind[] = [];
    const erow: number[] = [];
    for (let x = 0; x < width; x++) {
      trow.push(pickWeighted(rand, pal.ground));
      erow.push(0);
    }
    terrain.push(trow);
    elevation.push(erow);
  }

  const midY = Math.floor(height / 2);
  const corridorRow = (y: number) => Math.abs(y - midY) <= 1;

  // Plateaus: a few elevated regions with ramps on the corridor-adjacent side.
  if (pal.elevationVariance > 0) {
    const plateauCount = 1 + Math.floor(rand() * 3);
    for (let i = 0; i < plateauCount; i++) {
      if (rand() > pal.elevationVariance * 4) continue;
      const px = 4 + Math.floor(rand() * (width - 12));
      const py = rand() < 0.5
        ? Math.floor(height * 0.12 + rand() * height * 0.18)
        : Math.floor(height * 0.7 + rand() * height * 0.18);
      const pw = 4 + Math.floor(rand() * 6);
      const ph = 4 + Math.floor(rand() * 6);
      for (let dy = 0; dy < ph; dy++) {
        for (let dx = 0; dx < pw; dx++) {
          const x = px + dx;
          const y = py + dy;
          if (x < 1 || y < 1 || x >= width - 1 || y >= height - 1) continue;
          if (corridorRow(y)) continue;
          elevation[y][x] = 1;
        }
      }
      // Add a ramp on the south side of the plateau (closest to corridor).
      const rampDir = py < midY ? 's' : 'n';
      const rampY = py < midY ? py + ph : py - 1;
      const rampX = px + Math.floor(pw / 2);
      if (rampY > 0 && rampY < height - 1 && !corridorRow(rampY)) {
        const t: TerrainKind = rampDir === 's' ? 'ramp_s' : 'ramp_n';
        terrain[rampY][rampX] = t;
        elevation[rampY][rampX] = 0;
      }
    }
  }

  // Wall obstacles, never on corridor.
  const wallCount = Math.floor(width * height * 0.03 + depth * 1.2);
  for (let i = 0; i < wallCount; i++) {
    const x = Math.floor(rand() * width);
    const y = Math.floor(rand() * height);
    if (corridorRow(y)) continue;
    terrain[y][x] = pal.obstacle;
  }

  // Optional water patch.
  if (pal.water && rand() < 0.6) {
    const px = Math.floor(width * (0.25 + rand() * 0.5));
    const py = rand() < 0.5 ? Math.floor(height * 0.15) : Math.floor(height * 0.8);
    const pr = 2 + Math.floor(rand() * 3);
    for (let dy = -pr; dy <= pr; dy++) {
      for (let dx = -pr; dx <= pr; dx++) {
        if (dx * dx + dy * dy > pr * pr) continue;
        const x = px + dx;
        const y = py + dy;
        if (x < 1 || y < 1 || x >= width - 1 || y >= height - 1) continue;
        if (corridorRow(y)) continue;
        terrain[y][x] = pal.water!;
        elevation[y][x] = 0;
      }
    }
  }

  const playerStart = { x: 1, y: midY };
  const exit = { x: width - 2, y: midY };
  terrain[playerStart.y][playerStart.x] = 'grass';
  terrain[exit.y][exit.x] = 'grass';
  elevation[playerStart.y][playerStart.x] = 0;
  elevation[exit.y][exit.x] = 0;

  // Props scatter.
  const props: PropSpawn[] = [];
  const propCount = pal.propCount;
  let attempts = 0;
  while (props.length < propCount && attempts < propCount * 6) {
    attempts++;
    const x = Math.floor(rand() * width);
    const y = Math.floor(rand() * height);
    if (corridorRow(y)) continue;
    const t = terrain[y][x];
    const ts = TERRAIN[t];
    if (!ts.walkable) continue;
    if (props.some(p => p.x === x && p.y === y)) continue;
    // Don't drop props right at start/exit.
    if (Math.abs(x - playerStart.x) + Math.abs(y - playerStart.y) < 3) continue;
    if (Math.abs(x - exit.x) + Math.abs(y - exit.y) < 3) continue;
    props.push({ x, y, kind: pickWeighted(rand, pal.props) });
  }

  // Enemies — count scales with depth; types pulled from biome pool.
  const enemySpawns: { x: number; y: number; type: string }[] = [];
  const enemyCount = 5 + Math.min(12, depth * 2);
  let placedEnemies = 0;
  let eAttempts = 0;
  while (placedEnemies < enemyCount && eAttempts < 400) {
    eAttempts++;
    const x = Math.floor(6 + rand() * (width - 12));
    const y = Math.floor(2 + rand() * (height - 4));
    if (!TERRAIN[terrain[y][x]].walkable) continue;
    if (props.some(p => p.x === x && p.y === y && PROP_BLOCKING[p.kind])) continue;
    if (Math.abs(x - playerStart.x) < 6) continue;
    const type = pal.enemyPool[Math.floor(rand() * pal.enemyPool.length)];
    enemySpawns.push({ x, y, type });
    placedEnemies++;
  }

  // Loot.
  const lootSpawns: { x: number; y: number; item: string }[] = [];
  const lootCount = 3 + Math.floor(rand() * 4);
  const lootItems = ['sword', 'axe', 'bow', 'armor_light', 'ring', 'mace'];
  for (let i = 0; i < lootCount; i++) {
    const x = Math.floor(4 + rand() * (width - 8));
    const y = Math.floor(2 + rand() * (height - 4));
    if (!TERRAIN[terrain[y][x]].walkable) continue;
    if (props.some(p => p.x === x && p.y === y && PROP_BLOCKING[p.kind])) continue;
    lootSpawns.push({ x, y, item: lootItems[Math.floor(rand() * lootItems.length)] });
  }

  // Dungeon entrance — small chance per chunk, more common in deeper biomes.
  let dungeonEntrance: { x: number; y: number } | undefined;
  const entranceChance = chosenBiome === 'ruins' || chosenBiome === 'graveyard' ? 0.55
    : chosenBiome === 'forest' || chosenBiome === 'swamp' ? 0.3
    : 0.15;
  if (rand() < entranceChance) {
    let ex = 0, ey = 0;
    for (let i = 0; i < 30; i++) {
      const x = 6 + Math.floor(rand() * (width - 12));
      const y = midY + (rand() < 0.5 ? -1 : 1) * (3 + Math.floor(rand() * 6));
      if (y < 2 || y >= height - 2) continue;
      if (!TERRAIN[terrain[y][x]].walkable) continue;
      if (props.some(p => p.x === x && p.y === y)) continue;
      ex = x; ey = y; break;
    }
    if (ex) dungeonEntrance = { x: ex, y: ey };
  }

  return {
    id: `chunk_${depth}_${seed}`,
    kind: 'wilderness',
    biome: chosenBiome,
    depth,
    width,
    height,
    terrain,
    elevation,
    playerStart,
    exit,
    enemySpawns,
    lootSpawns,
    props,
    dungeonEntrance,
  };
}

/**
 * Dungeon chunk — narrow corridors framed by walls. depth ticks per chunk
 * inside; harder than surface biomes at equivalent distance.
 */
export function generateDungeonChunk({ depth, width, height, seed }: GenOpts): Chunk {
  const rand = mulberry32(seed + depth * 31 + 99991);
  const biome: Biome = rand() < 0.5 ? 'cave' : 'crypt';
  const pal = PALETTES[biome];
  const terrain: TerrainKind[][] = [];
  const elevation: number[][] = [];
  for (let y = 0; y < height; y++) {
    const trow: TerrainKind[] = [];
    const erow: number[] = [];
    for (let x = 0; x < width; x++) {
      // Walls along edges; floor inside.
      const onEdge = x < 2 || y < 2 || x >= width - 2 || y >= height - 2;
      trow.push(onEdge ? 'wall' : pickWeighted(rand, pal.ground));
      erow.push(0);
    }
    terrain.push(trow);
    elevation.push(erow);
  }
  // Scatter inner walls forming partial chambers.
  const wallCount = Math.floor(width * height * 0.06);
  const midY = Math.floor(height / 2);
  for (let i = 0; i < wallCount; i++) {
    const x = 3 + Math.floor(rand() * (width - 6));
    const y = 3 + Math.floor(rand() * (height - 6));
    if (Math.abs(y - midY) <= 1) continue;
    terrain[y][x] = 'wall';
  }

  const playerStart = { x: 2, y: midY };
  const exit = { x: width - 3, y: midY };
  terrain[playerStart.y][playerStart.x] = pal.ground[0].kind;
  terrain[exit.y][exit.x] = pal.ground[0].kind;

  // Props.
  const props: PropSpawn[] = [];
  let attempts = 0;
  while (props.length < pal.propCount && attempts < pal.propCount * 6) {
    attempts++;
    const x = Math.floor(rand() * width);
    const y = Math.floor(rand() * height);
    if (Math.abs(y - midY) <= 1) continue;
    if (!TERRAIN[terrain[y][x]].walkable) continue;
    if (props.some(p => p.x === x && p.y === y)) continue;
    props.push({ x, y, kind: pickWeighted(rand, pal.props) });
  }

  // Enemies — harder than surface at the same depth.
  const enemySpawns: { x: number; y: number; type: string }[] = [];
  const enemyCount = 6 + Math.min(14, depth * 2);
  let attempts2 = 0;
  while (enemySpawns.length < enemyCount && attempts2 < 400) {
    attempts2++;
    const x = Math.floor(6 + rand() * (width - 12));
    const y = Math.floor(3 + rand() * (height - 6));
    if (!TERRAIN[terrain[y][x]].walkable) continue;
    if (Math.abs(x - playerStart.x) < 6) continue;
    const type = pal.enemyPool[Math.floor(rand() * pal.enemyPool.length)];
    enemySpawns.push({ x, y, type });
  }

  // Loot — more generous in dungeons.
  const lootSpawns: { x: number; y: number; item: string }[] = [];
  const lootCount = 4 + Math.floor(rand() * 4);
  const pool = ['sword', 'axe', 'mace', 'bow', 'armor_light', 'armor_medium', 'ring', 'amulet'];
  for (let i = 0; i < lootCount; i++) {
    const x = Math.floor(4 + rand() * (width - 8));
    const y = Math.floor(3 + rand() * (height - 6));
    if (!TERRAIN[terrain[y][x]].walkable) continue;
    lootSpawns.push({ x, y, item: pool[Math.floor(rand() * pool.length)] });
  }

  return {
    id: `dungeon_${depth}_${seed}`,
    kind: 'dungeon',
    biome,
    depth,
    width,
    height,
    terrain,
    elevation,
    playerStart,
    exit,
    enemySpawns,
    lootSpawns,
    props,
  };
}

// ---- towns -----------------------------------------------------------------

const TOWN_NAMES = [
  'Greyford', 'Coldharbour', 'Stonefall', 'Whisperwood', 'Lastwatch',
  'Embergate', 'Hollowmere', 'Brackmoor', 'Ravenfen', 'Thornholt',
];

/**
 * Village chunk — palisaded clearing dotted with houses (rect arrangements
 * of wall tiles standing in for buildings) and 4 NPCs at fixed jobs you
 * can click to interact with.
 */
export function generateTownChunk({ depth, width, height, seed }: GenOpts): Chunk {
  const rand = mulberry32(seed + depth * 31 + 7777);
  const terrain: TerrainKind[][] = [];
  const elevation: number[][] = [];
  for (let y = 0; y < height; y++) {
    const trow: TerrainKind[] = [];
    const erow: number[] = [];
    for (let x = 0; x < width; x++) {
      trow.push(rand() < 0.55 ? 'dirt' : 'wood');
      erow.push(0);
    }
    terrain.push(trow);
    elevation.push(erow);
  }
  const midY = Math.floor(height / 2);
  // Palisade with one wide gate each side.
  for (let x = 2; x < width - 2; x++) {
    if (Math.abs(x - Math.floor(width / 2)) > 8) {
      terrain[1][x] = 'wall';
      terrain[height - 2][x] = 'wall';
    }
  }
  for (let y = 2; y < height - 2; y++) {
    if (Math.abs(y - midY) > 4) {
      terrain[y][2] = 'wall';
      terrain[y][width - 3] = 'wall';
    }
  }

  // Lay out a few houses: 4x4 wall rectangles with one tile cleared as a "door".
  const houses = [
    { x: 8, y: 6 }, { x: 18, y: 7 },
    { x: 8, y: height - 12 }, { x: 18, y: height - 11 },
    { x: 32, y: 8 }, { x: 32, y: height - 13 },
  ];
  for (const h of houses) {
    for (let dy = 0; dy < 4; dy++) {
      for (let dx = 0; dx < 4; dx++) {
        const x = h.x + dx;
        const y = h.y + dy;
        if (x < 2 || y < 2 || x >= width - 2 || y >= height - 2) continue;
        // Only the perimeter is wall; interior stays floor.
        const onEdge = dx === 0 || dy === 0 || dx === 3 || dy === 3;
        if (!onEdge) continue;
        if (Math.abs(y - midY) <= 1) continue;
        // Don't block exact corridor; otherwise place wall.
        terrain[y][x] = 'wall';
      }
    }
    // Door — clear one wall tile facing south (toward corridor).
    if (h.y + 3 < height && h.y + 3 > 0) {
      const doorX = h.x + 2;
      if (doorX < width - 2) terrain[h.y + 3][doorX] = 'wood';
    }
  }

  const playerStart = { x: 1, y: midY };
  const exit = { x: width - 2, y: midY };
  terrain[playerStart.y][playerStart.x] = 'dirt';
  terrain[exit.y][exit.x] = 'dirt';

  // Decorative props.
  const props: PropSpawn[] = [];
  for (let i = 0; i < 26; i++) {
    const x = Math.floor(4 + rand() * (width - 8));
    const y = Math.floor(3 + rand() * (height - 6));
    if (Math.abs(y - midY) <= 1) continue;
    if (terrain[y][x] !== 'dirt' && terrain[y][x] !== 'wood') continue;
    if (props.some(p => p.x === x && p.y === y)) continue;
    const kinds: PropKind[] = ['shrub', 'shrub', 'tree', 'rock'];
    props.push({ x, y, kind: kinds[Math.floor(rand() * kinds.length)] });
  }

  const townName = TOWN_NAMES[Math.floor(rand() * TOWN_NAMES.length)];

  // Place 4 NPCs at scattered floor tiles along the corridor edges.
  const npcs: NpcSpawn[] = [
    { x: 10, y: midY + 3,  role: 'vendor',     name: 'Marda the Trader',  greeting: '"Steel for coin. No haggling, sells fair."' },
    { x: 20, y: midY - 3,  role: 'innkeep',    name: 'Old Garrik',        greeting: '"A bed and a bowl of stew sets a soul right."' },
    { x: 30, y: midY + 3,  role: 'recruiter',  name: 'Captain Vellis',    greeting: '"Looking for hands. You looking for work?"' },
    { x: 40, y: midY - 3,  role: 'lorekeeper', name: 'Brother Hask',      greeting: '"Roads here have memories. Sit a while."' },
  ];
  // Snap each NPC onto a clear floor tile (push them off walls/water).
  for (const n of npcs) {
    let safe = 0;
    while (safe++ < 12 && !TERRAIN[terrain[n.y]?.[n.x] ?? 'wall'].walkable) {
      n.y += n.y > midY ? -1 : 1;
    }
  }

  return {
    id: `village_${depth}_${seed}`,
    kind: 'village',
    biome: 'plains',
    depth,
    width,
    height,
    terrain,
    elevation,
    playerStart,
    exit,
    enemySpawns: [],
    lootSpawns: [],
    props,
    npcs,
    townName,
  };
}
